import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useForm } from 'react-hook-form';
import { playlistsCreate, playlistsUpdate } from 'store/playlists/items';
import { reportMessage } from 'lib/error';
import { Notification } from '@schibsted-svp/react-ui';
import {
    getPreferredPlaylistType,
    getPlaylistType,
    getPlaylistLabels,
    getPlaylistMetadata,
    isPodcastOnlyPlaylist,
    mergePlaylistLabels,
    mapToBackendPlaylistItemsNotation,
    mapPlaylistMetadata,
    adjustItemsIndexes,
    isPlaylistOfPlaylists,
} from './utils';
import { resolver } from './validator';
import { Playlist, FormValues } from './types';

export const useFormValues = ({ playlist }: { playlist: Playlist }): FormValues =>
    useMemo(() => {
        if (!playlist) {
            return {
                name: '',
                header: null,
                query: '',
                limit: 10,
                items: [],
                labels: [],
                metadata: [],
                type: getPreferredPlaylistType(),
                searchQuery: '',
                partnerName: null,
                partnerPlaylistAlias: null,
                useLimitPerCategory: true,
            };
        }

        const searchQuery = new URLSearchParams(playlist.searchQuery || '');
        const rawMetadata = getPlaylistMetadata(playlist);
        const useLimitPerCategoryEntry = rawMetadata.find(({ key }) => key === 'useLimitPerCategory');
        const useLimitPerCategory = useLimitPerCategoryEntry ? useLimitPerCategoryEntry.value !== 'false' : true;
        const metadata = rawMetadata.filter(({ key }) => key !== 'useLimitPerCategory');

        return {
            name: playlist.name,
            header: playlist.header,
            query: searchQuery.get('query'),
            limit: Number(searchQuery.get('limit')) || 10,
            items: isPlaylistOfPlaylists(playlist) ? playlist.playlists : playlist.assets,
            searchQuery: playlist.searchQuery,
            type: getPlaylistType(playlist),
            labels: getPlaylistLabels(playlist),
            metadata,
            isPodcastPlaylist: isPodcastOnlyPlaylist(playlist),
            partnerName: playlist.partnerName,
            partnerPlaylistAlias: playlist.partnerPlaylistAlias,
            useLimitPerCategory,
        };
    }, [playlist]);

export const usePlaylistForm = ({ playlist }: { playlist: Playlist }) => {
    const formValues = useFormValues({ playlist });
    const formApi = useForm<FormValues>({
        defaultValues: formValues,
        values: formValues,
        resolver,
        mode: 'onChange',
    });

    const history = useHistory();
    const dispatch = useDispatch();

    const { handleSubmit, setError } = formApi;

    const onSubmit = (mode: 'new' | 'edit', provider: string, isRegularPlaylist: boolean) => async (values) => {
        try {
            const metadataWithLimit = values.useLimitPerCategory
                ? values.metadata
                : [...values.metadata, { key: 'useLimitPerCategory', value: 'false' }];

            const valuesToSend = {
                id: playlist?.id,
                provider,
                name: values.name,
                header: values.header,
                labels: mergePlaylistLabels(values.labels, values.type, values.isPodcastPlaylist),
                metadata: mapPlaylistMetadata(metadataWithLimit),
                searchQuery: values.searchQuery,
                items: mapToBackendPlaylistItemsNotation(values.items),
                ...(isRegularPlaylist && { assets: adjustItemsIndexes(values.items) }),
                partnerName: values.partnerName || null,
                partnerPlaylistAlias: values.partnerPlaylistAlias || null,
            };

            if (mode === 'new') {
                const { playlist: newPlaylist } = await dispatch(playlistsCreate(valuesToSend));
                Notification.notify.success('Successfully saved new playlist');
                history.push(`/${provider}/playlists/edit/${newPlaylist.id}`);
                return;
            }
            await dispatch(playlistsUpdate(valuesToSend));
            Notification.notify.success('Playlist updated successfully');
        } catch (error) {
            const apiErrors: string[] = error?.errors ?? [];
            const isPartnerUniqueConstraintError = apiErrors.some(
                (e) => typeof e === 'string' && e.includes('partnerName') && e.includes('partnerPlaylistAlias')
            );

            if (isPartnerUniqueConstraintError) {
                const message = 'This partner name and alias combination is already used by another playlist';
                setError('partnerName', { message });
                setError('partnerPlaylistAlias', { message });
                return;
            }

            const ERROR_MESSAGE = 'There was an error during playlist submission';
            reportMessage({
                message: ERROR_MESSAGE,
                error,
            });
            Notification.notify.error(ERROR_MESSAGE);
        }
    };

    return {
        formApi,
        onSubmit: (mode: 'new' | 'edit', provider: string, isRegularPlaylist: boolean) =>
            handleSubmit(onSubmit(mode, provider, isRegularPlaylist)),
    };
};
